import React, { useState, useMemo } from 'react';

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  green800: '#1a4a1c',
  green700: '#1e5e20',
  green600: '#2a7a2d',
  green500: '#3a9a3e',
  green200: '#b8e0bb',
  green100: '#dff0e0',
  green50:  '#f0f9f1',
  amber:    '#c8761a',
  bg:       '#f5f8f5',
  white:    '#ffffff',
  text:     '#1a2e1b',
  textMed:  '#4a6a4c',
  textLight:'#8aaa8c',
  border:   '#e0ece0',
  shadow:   'rgba(30,80,32,0.10)',
};

const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Parse a task ID → { year, monthName, sIdx, tIdx }
function parseTaskId(id) {
  const parts = id.split('-');
  return {
    year:      parseInt(parts[0]),
    monthName: parts.slice(1, parts.length - 2).join('-'),
    sIdx:      parseInt(parts[parts.length - 2]),
    tIdx:      parseInt(parts[parts.length - 1]),
  };
}

// Get task title + section info from zoneCustomizedData
function getTaskInfo(zoneData, monthName, sIdx, tIdx) {
  const md = zoneData[monthName];
  if (!md) return null;
  const section = md.sections[sIdx];
  if (!section) return null;
  const task = section.tasks[tIdx];
  if (!task) return null;
  return { title: task.text, cat: section.title, icon: section.icon };
}

// ── HistoryView ────────────────────────────────────────────────────────────────
const HistoryView = ({
  completedTasks,
  zoneCustomizedData,
  activeYear,
  isDesktop = false,
}) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(activeYear || currentYear);

  // Available years from completedTasks
  const availableYears = useMemo(() => {
    const yearSet = new Set([currentYear]);
    completedTasks.forEach(id => {
      const { year } = parseTaskId(id);
      if (!isNaN(year)) yearSet.add(year);
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [completedTasks, currentYear]);

  // Monthly completion data for selected year
  const monthlyStats = useMemo(() => {
    // Build total task counts from zone data
    const stats = MONTHS_FULL.map(month => {
      const md = zoneCustomizedData[month];
      const total = md
        ? md.sections.reduce((sum, s) => sum + s.tasks.length, 0)
        : 0;
      return { month, total, completed: 0, pct: 0 };
    });

    // Count completions for the selected year
    completedTasks.forEach(id => {
      const { year, monthName } = parseTaskId(id);
      if (year !== selectedYear) return;
      const idx = MONTHS_FULL.indexOf(monthName);
      if (idx !== -1 && stats[idx]) stats[idx].completed++;
    });

    // Calculate percentages
    stats.forEach(s => {
      s.pct = s.total > 0 ? Math.round((s.completed / s.total) * 100) : 0;
    });

    return stats;
  }, [completedTasks, zoneCustomizedData, selectedYear]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const total = Array.from(completedTasks).filter(id => {
      const { year } = parseTaskId(id);
      return year === selectedYear;
    }).length;

    const bestMonth = monthlyStats.reduce(
      (best, s) => (s.pct > (best?.pct || 0) ? s : best),
      null
    );

    // Streak: consecutive months with >0% completion, going back from current month
    let streak = 0;
    const nowMonth = new Date().getMonth();
    for (let i = nowMonth; i >= 0; i--) {
      if (monthlyStats[i]?.pct > 0) streak++;
      else break;
    }

    return { total, bestMonth, streak };
  }, [completedTasks, monthlyStats, selectedYear]);

  // Activity timeline: completed tasks grouped by month (desc)
  const timeline = useMemo(() => {
    const groups = {};
    completedTasks.forEach(id => {
      const { year, monthName, sIdx, tIdx } = parseTaskId(id);
      if (year !== selectedYear) return;
      const key = `${year}-${MONTHS_FULL.indexOf(monthName).toString().padStart(2, '0')}-${monthName}`;
      if (!groups[key]) groups[key] = { year, monthName, monthIdx: MONTHS_FULL.indexOf(monthName), items: [] };
      const info = getTaskInfo(zoneCustomizedData, monthName, sIdx, tIdx);
      if (info) groups[key].items.push(info);
    });

    return Object.values(groups)
      .sort((a, b) => b.monthIdx - a.monthIdx);
  }, [completedTasks, zoneCustomizedData, selectedYear]);

  const maxPct = Math.max(...monthlyStats.map(s => s.pct), 1);

  return (
    <div style={{
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      ...(isDesktop ? {} : { padding: '16px 16px 16px' }),
    }}>

      {/* ── Year selector ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: isDesktop ? 20 : 16,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.textMed }}>Year:</span>
        {availableYears.map(yr => (
          <div
            key={yr}
            onClick={() => setSelectedYear(yr)}
            style={{
              padding: '5px 14px', borderRadius: 99, cursor: 'pointer',
              background: yr === selectedYear ? T.green700 : T.white,
              color:      yr === selectedYear ? T.white    : T.textMed,
              fontSize: 13, fontWeight: 600,
              border: `1px solid ${yr === selectedYear ? 'transparent' : T.border}`,
              transition: 'all 0.15s',
            }}
          >
            {yr}
          </div>
        ))}
      </div>

      {/* ── Summary stats row ──────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
        gap: isDesktop ? 12 : 8,
        marginBottom: isDesktop ? 20 : 16,
      }}>
        {[
          {
            value: summaryStats.total,
            label: 'Tasks Done',
            color: T.green700,
            icon: '✓',
          },
          {
            value: summaryStats.bestMonth?.pct > 0 ? `${summaryStats.bestMonth.pct}%` : '—',
            label: summaryStats.bestMonth?.pct > 0
              ? `Best: ${MONTHS_SHORT[MONTHS_FULL.indexOf(summaryStats.bestMonth.month)]}`
              : 'Best Month',
            color: T.amber,
            icon: '🏆',
          },
          {
            value: summaryStats.streak,
            label: summaryStats.streak === 1 ? 'mo. streak' : 'mo. streak',
            color: '#1a6fa8',
            icon: '🔥',
          },
        ].map(({ value, label, color, icon }) => (
          <div
            key={label}
            style={{
              background: T.white, borderRadius: 14, padding: isDesktop ? '14px 16px' : '12px 14px',
              boxShadow: `0 1px 4px ${T.shadow}`,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <div style={{ fontSize: isDesktop ? 11 : 10 }}>{icon}</div>
            <div style={{ fontSize: isDesktop ? 22 : 20, fontWeight: 700, color, lineHeight: 1 }}>
              {value}
            </div>
            <div style={{ fontSize: isDesktop ? 11 : 10, color: T.textLight, fontWeight: 500 }}>
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Bar chart ─────────────────────────────────────────────────── */}
      <div style={{
        background: T.white, borderRadius: 16, padding: isDesktop ? '18px 20px' : '16px',
        boxShadow: `0 2px 12px ${T.shadow}`,
        marginBottom: isDesktop ? 20 : 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Monthly Completion — {selectedYear}</span>
          {summaryStats.total === 0 && (
            <span style={{ fontSize: 11, color: T.textLight, fontWeight: 400 }}>
              No tasks completed yet
            </span>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: isDesktop ? 10 : 6,
          height: isDesktop ? 120 : 96,
        }}>
          {monthlyStats.map(({ month, pct, completed, total }, idx) => {
            const barH    = maxPct > 0 ? Math.max(pct / maxPct * (isDesktop ? 80 : 64), pct > 0 ? 4 : 0) : 0;
            const isCurrent = idx === new Date().getMonth() && selectedYear === currentYear;
            return (
              <div
                key={month}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 3,
                }}
                title={`${month}: ${completed}/${total} tasks (${pct}%)`}
              >
                {/* Percentage label */}
                <div style={{
                  fontSize: isDesktop ? 10 : 9, color: pct > 0 ? T.green700 : 'transparent',
                  fontWeight: 600, height: isDesktop ? 14 : 12,
                }}>
                  {pct > 0 ? `${pct}%` : ''}
                </div>
                {/* Bar */}
                <div style={{
                  width: '100%',
                  height: isDesktop ? 80 : 64,
                  display: 'flex', alignItems: 'flex-end',
                }}>
                  <div style={{
                    width: '100%',
                    height: barH,
                    background: pct === 100
                      ? T.green700
                      : pct > 0
                        ? T.green600
                        : T.border,
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.4s ease',
                    border: isCurrent && pct === 0 ? `1px solid ${T.green200}` : 'none',
                  }} />
                </div>
                {/* Month label */}
                <div style={{
                  fontSize: isDesktop ? 10 : 9,
                  color: isCurrent ? T.green700 : T.textLight,
                  fontWeight: isCurrent ? 700 : 400,
                }}>
                  {MONTHS_SHORT[idx]}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Activity timeline ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>
          Activity
        </div>

        {timeline.length === 0 ? (
          <div style={{
            background: T.white, borderRadius: 14, padding: '28px 20px',
            textAlign: 'center', boxShadow: `0 1px 4px ${T.shadow}`,
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🌱</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.textMed }}>
              No completed tasks in {selectedYear}
            </div>
            <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>
              Complete tasks to see your history here
            </div>
          </div>
        ) : (
          timeline.map(({ monthName, year, items }) => (
            <div key={`${year}-${monthName}`} style={{ marginBottom: 16 }}>
              {/* Month group header */}
              <div style={{
                fontSize: 11, fontWeight: 700, color: T.textLight,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                marginBottom: 6,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>{monthName} {year}</span>
                <div style={{ flex: 1, height: 1, background: T.border }} />
                <span style={{ color: T.green700 }}>{items.length} done</span>
              </div>

              {/* Task items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {items.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      background: T.white, borderRadius: 10,
                      padding: isDesktop ? '10px 14px' : '9px 12px',
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      boxShadow: `0 1px 3px ${T.shadow}`,
                    }}
                  >
                    <div style={{
                      width: 18, height: 18, borderRadius: 5,
                      background: T.green600, flexShrink: 0, marginTop: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="10" height="8" viewBox="0 0 12 10">
                        <polyline points="1,5 4,9 11,1" stroke="white" strokeWidth="2"
                          fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, fontWeight: 600, color: T.text,
                        lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>
                        {item.icon} {item.cat}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HistoryView;
